import {makeAutoObservable, runInAction} from 'mobx';
import {networkOptimization, fileProcessingStreamPersistence} from '../../../../lib/api';
import {ProcessingFile, FileEvent, FileListResponse, FileDetailResponse, FileProcessingEvent, STATUS_MAPS, getStageDisplayName} from '../types';
import {transformFileList, transformFileDetail, mapEventStatusToStageStatus, formatDate} from '../file-transformers';
import {safeConsoleError, safeConsoleLog} from '../../../../lib/sanitize';
import {prepareErrorReportDownload, streamErrorReport} from '../services/error-report-download-service';

/**
 * Manages the processing files list with pagination, polling, and file actions.
 * Handles loading file details, renaming, deleting, and continuing processing.
 */
export class FileListStore {
    private _processingFiles: ProcessingFile[] = [];
    private _processingFilesPagination: {
        currentPage: number;
        totalPages: number;
        totalRows: number;
        pageSize: number;
    } | null = null;
    private _loadingProcessingFiles = false;
    private _isPolling = false;
    isFileActionLoading = false;
    private _fileListPollingInterval: NodeJS.Timeout | null = null;
    private _isPollInFlight = false;
    private _pollAbortController: AbortController | null = null;
    isDownloading = false;
    downloadProgress: number | null = null;
    downloadError: string | null = null;
    downloadUsedFallback = false;
    cancelDownload: (() => void) | null = null;
    private readonly POLLING_INTERVAL_MS = 30000; // 30 seconds

    constructor(
        private filesService: any,
        private sseConnectionStore: any,
        private showToastNotification: (kind: 'success' | 'error' | 'info' | 'warning', title: string, subtitle: string, options?: {errorId?: string; action?: {label: string; onClick: () => void}; timeout?: number}) => void,
        private networkOptimizationApi: any,
        private isAbortError: (error: unknown) => boolean
    ) {
        makeAutoObservable(this, {
            _isPolling: false,
        } as any);
        // Clean up old file processing streams on initialization
        fileProcessingStreamPersistence.cleanupOldStreams();
    }

    get processingFiles(): ProcessingFile[] {
        return this._processingFiles;
    }

    get processingFilesPagination(): {
        currentPage: number;
        totalPages: number;
        totalRows: number;
        pageSize: number;
    } | null {
        return this._processingFilesPagination;
    }

    get totalProcessingFiles(): number {
        return this._processingFilesPagination?.totalRows ?? 0;
    }

    get loadingProcessingFiles(): boolean {
        return this._loadingProcessingFiles;
    }

    /**
     * Load processing files from API with pagination
     * @param controller AbortController for cancelling request
     * @param page Page number (1-indexed)
     * @param pageSize Number of items per page
     */
    async loadProcessingFiles(controller: AbortController, page = 1, pageSize = 20) {
        // Prevent duplicate loads if already loading
        if (this._loadingProcessingFiles) {
            return;
        }

        try {
            runInAction(() => {
                this._loadingProcessingFiles = true;
            });

            // Optimistically update pagination immediately for responsive UI
            runInAction(() => {
                if (this._processingFilesPagination) {
                    this._processingFilesPagination = {
                        ...this._processingFilesPagination,
                        currentPage: page,
                        pageSize: pageSize
                    };
                }
            });

            const response: FileListResponse = await this.filesService.listFiles(page, pageSize, controller.signal);

            runInAction(() => {
                this._processingFiles = transformFileList(response);
                this._processingFilesPagination = {
                    currentPage: response.current_page,
                    totalPages: response.total_pages,
                    totalRows: response.total_rows,
                    pageSize: response.page_size
                };
                this._loadingProcessingFiles = false;
            });

            // Start polling for file list updates (replaces SSE)
            this.startFileListPolling();
        } catch (error: any) {
            if (error?.name === 'AbortError') {
                return;
            }

            runInAction(() => {
                this._loadingProcessingFiles = false;
                this.showToastNotification('error', 'Failed to load files', 'Unable to fetch processing files from the server');
            });
        }
    }

    /**
     * Load detailed file information including full event history
     * Updates the file in the store with complete data
     * @param fileId File upload ID
     * @param controller AbortController for cancelling request
     * @returns Transformed ProcessingFile or null on error
     */
    async loadFileDetail(fileId: number, controller: AbortController): Promise<{file: ProcessingFile; events: FileEvent[]} | null> {
        safeConsoleLog(`[loadFileDetail] Loading file detail for file ${fileId}`);
        try {
            const response: FileDetailResponse = await this.filesService.getFileDetail(fileId, controller.signal);
            safeConsoleLog(`[loadFileDetail] API response received:`, response);

            const transformed = transformFileDetail(response);
            safeConsoleLog(`[loadFileDetail] Transformed file:`, transformed);

            runInAction(() => {
                const index = this._processingFiles.findIndex(f => f.id === transformed.id);
                if (index !== -1) {
                    this._processingFiles[index] = transformed;
                    safeConsoleLog(`[loadFileDetail] Updated file at index ${index}`);
                } else {
                    safeConsoleLog(`[loadFileDetail] File ${transformed.id} not found in store, skipping update`);
                }
            });

            return {file: transformed, events: response.events};
        } catch (error: any) {
            if (error?.name === 'AbortError') {
                safeConsoleLog(`[loadFileDetail] Request aborted for file ${fileId}`);
                return null;
            }

            safeConsoleError(`[loadFileDetail] FAILED for file ${fileId}:`, error);
            safeConsoleError('[loadFileDetail] Error details:', JSON.stringify({
                name: error?.name,
                message: error?.message,
                status: error?.status,
                statusCode: error?.statusCode,
                response: error?.response,
                apiError: error?.apiError
            }, null, 2));

            this.showToastNotification('error', 'Failed to load file details', 'Unable to fetch file details from the server');
            return null;
        }
    }

    /**
     * Update a specific file's properties in the store
     * Used by SSE handlers to update file status in real-time
     * @param fileId File upload ID
     * @param updates Partial file object with properties to update
     */
    updateFileStatus(fileId: string, updates: Partial<ProcessingFile>) {
        const fileIndex = this._processingFiles.findIndex(f => f.id === fileId);
        if (fileIndex !== -1) {
            this._processingFiles[fileIndex] = {
                ...this._processingFiles[fileIndex],
                ...updates
            };
        }
    }

    /**
     * Update processing file status in the store
     */
    setProcessingFileStatus(fileId: string, status: string): void {
        runInAction(() => {
            const idx = this._processingFiles.findIndex((f: ProcessingFile) => f.id === fileId);
            if (idx !== -1) {
                this._processingFiles[idx] = {
                    ...this._processingFiles[idx],
                    status: status as any,
                };
            }
        });
    }

    /**
     * Handle incoming SSE file processing event and update file state
     * Updates stages, activity, error details, and shows toast notifications
     */
    handleFileProcessingEvent(fileId: string, event: FileProcessingEvent): void {
        const {
            id,
            stage_name,
            status,
            message,
            event_ts,
            details
        } = event;

        const stageKey = STATUS_MAPS.stageName(stage_name);
        if (!stageKey) {
            safeConsoleError(`Unknown stage: ${stage_name}`);
            return;
        }

        const stageStatus = mapEventStatusToStageStatus(status);
        const stageName = getStageDisplayName(stageKey);

        // Check if this is a NEW event (not already seen from initial API call)
        const connection = this.sseConnectionStore.processingDetailsSSEConnections.get(parseInt(fileId));

        // Ensure id is a number to match the type in Set<number>
        const eventId: number | null = id ? Number(id) : null;

        // Check if this event was already seen from the initial API call
        const isNewEvent = eventId ? !connection?.seenEventIds?.has(eventId) : true;

        // Add event ID to seen set (as number)
        if (eventId && connection?.seenEventIds) {
            connection.seenEventIds.add(eventId);
        }

        runInAction(() => {
            const fileIndex = this._processingFiles.findIndex(f => f.id === fileId);
            if (fileIndex === -1) return;

            const file = this._processingFiles[fileIndex];

            // Create new stages object to trigger reactivity
            const updatedStages = {...file.stages, [stageKey]: stageStatus};

            // Create updated activity array
            let updatedActivity = file.activity;
            if (message && event_ts) {
                const formattedTime = formatDate(event_ts);
                const exists = file.activity.some(a => a.time === formattedTime && a.message === message);
                if (!exists) {
                    // Calculate end time and duration if available from SSE event
                    let endTime: string | undefined;
                    let duration: string | undefined;
                    const durationSecondsRaw = details?.duration_seconds || event.duration_seconds;
                    const durationSeconds = durationSecondsRaw ? Number(durationSecondsRaw) : 0;

                    if (durationSeconds && durationSeconds > 0) {
                        const startDate = new Date(event_ts);
                        const endDate = new Date(startDate.getTime() + durationSeconds * 1000);
                        endTime = formatDate(endDate.toISOString());

                        // Format duration
                        const totalSeconds = durationSeconds;
                        if (totalSeconds < 60) {
                            duration = `${totalSeconds.toFixed(1)}s`;
                        } else if (totalSeconds < 3600) {
                            const minutes = Math.floor(totalSeconds / 60);
                            const seconds = Math.floor(totalSeconds % 60);
                            duration = seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
                        } else {
                            const hours = Math.floor(totalSeconds / 3600);
                            const minutes = Math.floor((totalSeconds % 3600) / 60);
                            duration = minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
                        }
                    }

                    updatedActivity = [
                        {
                            time: formattedTime,
                            message,
                            timestamp: event_ts,
                            startTime: formattedTime,
                            endTime,
                            duration
                        },
                        ...file.activity
                    ];
                }
            }

            // Handle error details
            let updatedErrorDetails = file.errorDetails;
            if (stageStatus === 'failed' && details) {
                try {
                    const parsedDetails = typeof details === 'string' ? JSON.parse(details) : details;
                    if (!file.errorDetails) {
                        updatedErrorDetails = {
                            issuesFound: message || 'Processing failed',
                            rowsWithIssues: parsedDetails.validation_errors || 0,
                            cellsWithIssues: parsedDetails.validation_warnings || 0,
                            errors: message ? [message] : []
                        };
                    } else {
                        updatedErrorDetails = {
                            ...file.errorDetails,
                            errors: message ? [...file.errorDetails.errors, message] : file.errorDetails.errors
                        };
                    }
                } catch (error) {
                    safeConsoleError('Failed to parse error details:', error);
                }
            }

            // Determine updated stage text and status
            let updatedStage = file.stage;
            let updatedStatus = file.status;

            if (stageStatus === 'processing') {
                updatedStage = stageName;
                // If any stage (except file upload) starts processing, update file status to 'processing'
                if (file.status === 'queued' && stageKey !== 'fileUpload') {
                    updatedStatus = 'processing';
                }
            }

            if (stageStatus === 'failed') {
                updatedStatus = 'needs-review';
            }

            // Create completely new file object with all updates
            const updatedFile = {
                ...file,
                stages: updatedStages,
                stage: updatedStage,
                status: updatedStatus,
                activity: updatedActivity,
                errorDetails: updatedErrorDetails
            };

            // Use splice for MobX reactivity
            this._processingFiles.splice(fileIndex, 1, updatedFile);

            // Show toast notifications ONLY for new events (not replayed from initial API call)
            if (isNewEvent) {
                if (stageStatus === 'complete') {
                    this.showToastNotification(
                        'success',
                        `${stageName} Complete`,
                        `${file.fileName} - ${message}`,
                        {timeout: 5000}
                    );
                } else if (stageStatus === 'failed') {
                    this.showToastNotification(
                        'error',
                        `${stageName} Failed`,
                        `${file.fileName} - ${message}`,
                        {
                            errorId: fileId,
                            action: {
                                label: 'View Error Report',
                                onClick: () => this.downloadErrorReport(Number(fileId))
                            },
                            timeout: 0
                        }
                    );
                }
            }
        });
    }

    /**
     * Start polling for file list updates (replaces multi-file SSE)
     * Polls every 30 seconds to update processing file statuses
     * Only starts if there are files in active states (queued or processing)
     */
    startFileListPolling() {
        // Only start if not already polling
        if (this._isPolling) {
            return;
        }

        // Check if there are any files in active states (queued or processing)
        const hasActiveFiles = this._processingFiles.some(
            f => f.status === 'queued' || f.status === 'processing'
        );
        if (!hasActiveFiles) {
            return;
        }

        this._isPolling = true;

        safeConsoleLog(`[Polling] Started file list polling (every ${this.POLLING_INTERVAL_MS / 1000}s)`);

        // Set up polling interval
        this._fileListPollingInterval = setInterval(async () => {
            await this.pollFileListUpdates();
        }, this.POLLING_INTERVAL_MS);
    }

    /**
     * Poll for file list updates and merge changes
     * Shows toast notifications for status changes
     * @private
     */
    private async pollFileListUpdates() {
        if (!this._isPolling || this._processingFiles.length === 0) {
            return;
        }

        // Prevent overlapping requests
        if (this._isPollInFlight) {
            safeConsoleLog('[Polling] Skipping poll - previous request still in flight');
            return;
        }

        this._isPollInFlight = true;

        // Create abort controller for this poll request
        this._pollAbortController = new AbortController();

        // Clean up old file processing streams periodically
        fileProcessingStreamPersistence.cleanupOldStreams();

        try {
            // Fetch latest file list with same pagination
            const currentPage = this._processingFilesPagination?.currentPage || 1;
            const pageSize = this._processingFilesPagination?.pageSize || 20;

            const response: FileListResponse = await this.filesService.listFiles(currentPage, pageSize, this._pollAbortController.signal);
            const updatedFiles = transformFileList(response);

            runInAction(() => {
                // Update pagination info
                this._processingFilesPagination = {
                    currentPage: response.current_page,
                    totalPages: response.total_pages,
                    totalRows: response.total_rows,
                    pageSize: response.page_size
                };

                // Build map of current files for efficient lookup of detailed data
                const currentFilesMap = new Map(this._processingFiles.map(f => [f.id, f]));

                // Process each file from the API response and merge with existing detailed data
                const mergedFiles = updatedFiles.map(updatedFile => {
                    const currentFile = currentFilesMap.get(updatedFile.id);

                    if (currentFile) {
                        // File exists - check for status changes and preserve detailed data
                        const statusChanged = currentFile.status !== updatedFile.status;

                        // Preserve detailed data if:
                        // 1. File has detailsLoaded flag (loaded via loadFileDetail API), OR
                        // 2. File has active SSE connection (receiving real-time updates)
                        const hasActiveSSEConnection = this.sseConnectionStore.processingDetailsSSEConnections.has(parseInt(updatedFile.id));
                        const shouldPreserveDetails = currentFile.detailsLoaded === true || hasActiveSSEConnection;

                        const mergedFile = shouldPreserveDetails ? {
                            ...updatedFile,
                            // Preserve detailed activity and stages from loadFileDetail or SSE
                            activity: currentFile.activity,
                            stages: currentFile.stages,
                            errorDetails: currentFile.errorDetails || updatedFile.errorDetails,
                            detailsLoaded: currentFile.detailsLoaded || false
                        } : updatedFile;

                        // Show toast for terminal state changes
                        if (statusChanged) {
                            if (updatedFile.status === 'completed') {
                                this.showToastNotification(
                                    'success',
                                    'File Processing Complete',
                                    `${updatedFile.fileName} is ready in Available Files`,
                                    {timeout: 10000}
                                );
                            } else if (updatedFile.status === 'needs-review') {
                                this.showToastNotification(
                                    'error',
                                    'File Processing Failed',
                                    `${updatedFile.fileName} encountered errors during processing`,
                                    {
                                        errorId: updatedFile.id,
                                        action: {
                                            label: 'View Error Report',
                                            onClick: () => this.downloadErrorReport(Number(updatedFile.id))
                                        },
                                        timeout: 0
                                    }
                                );
                            }
                        }

                        return mergedFile;
                    }

                    // New file - return as-is
                    return updatedFile;
                });

                // Replace entire array with updated list (maintains backend ordering)
                this._processingFiles = mergedFiles;

                // Log polling update for debugging
                safeConsoleLog(`[Polling] Updated processing files table: ${mergedFiles.length} files, ${response.total_rows} total`);

                // Stop polling if all files are in terminal state
                const hasActiveFiles = this._processingFiles.some(
                    f => f.status === 'queued' || f.status === 'processing'
                );
                if (!hasActiveFiles) {
                    safeConsoleLog('[Polling] All files in terminal state, stopping polling');
                    this.stopFileListPolling();
                }
            });
        } catch (error) {
            // Silently handle polling errors to avoid spamming user with toasts
            safeConsoleError('File list polling error:', error);
        } finally {
            this._isPollInFlight = false;
            this._pollAbortController = null;
        }
    }

    /**
     * Stop polling for file list updates
     * Called on component unmount
     */
    stopFileListPolling() {
        if (this._fileListPollingInterval) {
            clearInterval(this._fileListPollingInterval);
            this._fileListPollingInterval = null;
        }
        // Abort any in-flight polling request
        if (this._pollAbortController) {
            this._pollAbortController.abort();
            this._pollAbortController = null;
        }
        this._isPolling = false;
    }

    /**
     * Disconnect ALL file-related streams and polling (both table and details)
     * Called on page navigation or component unmount
     */
    disconnectAllFileStreams() {
        this.stopFileListPolling();
        this.sseConnectionStore.disconnectAllProcessingDetailsStreams();
    }

    /**
     * Continue processing a file with validation errors, using only valid rows
     */
    async continueProcessing(fileId: string, controller: AbortController): Promise<void> {
        runInAction(() => {
            this.isFileActionLoading = true;
        });
        try {
            const response = await networkOptimization.continueProcessing(fileId, controller.signal);
            runInAction(() => {
                const idx = this._processingFiles.findIndex((f: ProcessingFile) => f.id === fileId);
                if (idx !== -1) {
                    this._processingFiles[idx] = {
                        ...this._processingFiles[idx],
                        status: response.status ?? 'processing',
                        stage: response.current_stage ?? 'validation',
                    };
                }
                this.isFileActionLoading = false;
            });
            this.showToastNotification('success', 'Processing resumed', 'File is continuing with valid rows');
        } catch (error) {
            runInAction(() => {
                this.isFileActionLoading = false;
            });
            throw error;
        }
    }

    /**
     * Rename a processing file
     */
    async renameFile(fileId: string, filename: string, controller: AbortController, setRenamingFileId: (id: string | null) => void): Promise<string> {
        runInAction(() => {
            this.isFileActionLoading = true;
        });
        setRenamingFileId(fileId);
        try {
            const response = await networkOptimization.renameFile(fileId, filename, controller.signal);
            runInAction(() => {
                const idx = this._processingFiles.findIndex(f => f.id === fileId);
                if (idx !== -1) {
                    this._processingFiles[idx] = {
                        ...this._processingFiles[idx],
                        fileName: response.filename,
                    };
                }
                this.isFileActionLoading = false;
            });
            setRenamingFileId(null);
            this.showToastNotification('success', 'File renamed', `Renamed to ${response.filename}`);
            return response.filename;
        } catch (error) {
            runInAction(() => {
                this.isFileActionLoading = false;
            });
            setRenamingFileId(null);
            throw error;
        }
    }

    /**
     * Delete a processing file permanently
     */
    async deleteFile(fileId: string, filename: string, controller: AbortController) {
        runInAction(() => {
            this.isFileActionLoading = true;
        });
        try {
            await networkOptimization.deleteFile(fileId, filename, controller.signal);
            runInAction(() => {
                this._processingFiles = this._processingFiles.filter((f: ProcessingFile) => f.id !== fileId);
                this.isFileActionLoading = false;
            });
            this.showToastNotification('success', 'File deleted', `${filename} has been permanently deleted`);
        } catch (error) {
            runInAction(() => {
                this.isFileActionLoading = false;
            });
            throw error;
        }
    }

    /**
     * Download error report for a failed file
     * Shows download progress and handles both streaming and fallback responses
     */
    async downloadErrorReport(fileUploadId: number) {
        runInAction(() => {
            this.isDownloading = true;
            this.downloadProgress = null;
            this.downloadError = null;
            this.downloadUsedFallback = false;
            this.cancelDownload = null;
        });

        try {
            const controller = new AbortController();
            runInAction(() => {
                this.cancelDownload = () => controller.abort();
            });

            const file = this._processingFiles.find(f => f.id === String(fileUploadId));

            // Phase 1: Open file picker
            const prepareResult = await prepareErrorReportDownload(file, fileUploadId);
            if (prepareResult.cancelled) {
                runInAction(() => {
                    this.isDownloading = false;
                });
                return;
            }

            runInAction(() => {
                this.downloadUsedFallback = prepareResult.usedFallback;
            });

            // Phase 2: Stream download
            await streamErrorReport({
                fileUploadId,
                fileHandle: prepareResult.fileHandle,
                suggestedName: prepareResult.suggestedName,
                signal: controller.signal,
                onProgress: (bytes) => runInAction(() => {
                    this.downloadProgress = bytes;
                })
            });

            runInAction(() => {
                this.isDownloading = false;
                this.downloadProgress = null;
            });

            this.showToastNotification('success', 'Download Complete', `${prepareResult.suggestedName} downloaded successfully`);
        } catch (error: any) {
            if (this.isAbortError(error)) {
                // User cancelled - clear state and show cancellation message
                runInAction(() => {
                    this.isDownloading = false;
                    this.downloadProgress = null;
                    this.downloadUsedFallback = false;
                    this.cancelDownload = null;
                });
                this.showToastNotification('info', 'Download Cancelled', 'Error report download was cancelled');
                return;
            }

            runInAction(() => {
                this.downloadError = 'Download failed';
                this.isDownloading = false;
            });
            safeConsoleError('Error downloading error report:', error);
            this.showToastNotification('error', 'Download Failed', 'Download failed');
        } finally {
            runInAction(() => {
                this.downloadProgress = null;
            });
        }
    }

    clearDownloadState() {
        runInAction(() => {
            this.downloadError = null;
            this.downloadProgress = null;
            this.isDownloading = false;
            this.downloadUsedFallback = false;
            this.cancelDownload = null;
        });
    }
}
